/**
 * SpeyBooks - Settings Layout
 * 
 * Horizontal tab navigation for settings pages.
 * Uses react-router Outlet for nested routing.
 * 
 * Route: /settings/*
 */

import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { organisationApi, billingApi } from '@/lib/api';
import { cn } from '@/lib/utils';

// API to get key count
const getApiKeyCount = async () => {
  const response = await fetch('/api/v1/api-keys', {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
    },
  });
  if (!response.ok) return 0;
  const data = await response.json();
  return data.data?.keys?.filter((k: any) => k.isActive)?.length || 0;
};

interface TabBadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'muted';
}

function TabBadge({ children, variant = 'muted' }: TabBadgeProps) {
  return (
    <span
      className={cn(
        'ml-2 px-2 py-0.5 text-xs font-medium rounded-full',
        variant === 'success' && 'bg-accent-tint text-accent-strong',
        variant === 'muted' && 'bg-surface-tertiary text-content-tertiary'
      )}
    >
      {children}
    </span>
  );
}

interface TabLinkProps {
  to: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
  end?: boolean;
}

function TabLink({ to, children, badge, end }: TabLinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative px-1 py-4 text-sm font-medium transition-colors whitespace-nowrap',
          'hover:text-content',
          isActive
            ? 'text-heather-600 dark:text-heather-400'
            : 'text-content-tertiary'
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className="flex items-center">
            {children}
            {badge}
          </span>
          {isActive && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-heather-600 dark:bg-heather-400 rounded-full" />
          )}
        </>
      )}
    </NavLink>
  );
}

export default function SettingsLayout() {
  // Fetch organisation for VAT badge
  const { data: orgData } = useQuery({
    queryKey: ['organisation'],
    queryFn: () => organisationApi.get(),
  });

  // Fetch billing for plan badge
  const { data: billingData } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => billingApi.getStatus(),
  });

  // Fetch API key count
  const { data: keyCount } = useQuery({
    queryKey: ['api-keys', 'count'],
    queryFn: getApiKeyCount,
  });

  const isVatRegistered = orgData?.data?.settings?.vatRegistered;
  const currentPlan = billingData?.data?.plan;
  const planLabel = currentPlan === 'limited_company' ? 'Ltd Co.' : 
                    currentPlan === 'sole_trader' ? 'Sole Trader' : 
                    'Trial';

  return (
    // The settings sheet: caps the heading, the tabs, and every settings page to the
    // 1320 sheet with 26px gutters (1268 content), the dashboard's width rhythm, so the
    // four tabs hold one width and the bands line up with the tabs above them.
    <div className="space-y-6 max-w-[1320px] mx-auto px-[26px]">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-content">Settings</h1>
        <p className="mt-1 text-sm text-content-tertiary">
          Manage your account and preferences
        </p>
      </div>

      {/* Horizontal Tabs */}
      <div className="border-b border-edge">
        <nav className="flex gap-6 -mb-px overflow-x-auto">
          <TabLink to="/settings" end>
            General
          </TabLink>
          
          <TabLink 
            to="/settings/organisation"
            badge={isVatRegistered ? <TabBadge variant="success">VAT</TabBadge> : undefined}
          >
            Organisation
          </TabLink>
          
          <TabLink 
            to="/settings/developer"
            badge={keyCount ? <TabBadge>{keyCount} {keyCount === 1 ? 'key' : 'keys'}</TabBadge> : undefined}
          >
            Developer
          </TabLink>
          
          <TabLink 
            to="/settings/billing"
            badge={<TabBadge>{planLabel}</TabBadge>}
          >
            Billing
          </TabLink>
        </nav>
      </div>

      {/* Tab Content */}
      <Outlet />
    </div>
  );
}
